import { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { ProcessStep } from '../types';

interface ProcessSectionProps {
  steps: ProcessStep[];
  className?: string;
}

const ProcessSection = ({ steps, className = '' }: ProcessSectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredStep, setHoveredStep] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      id="process"
      ref={sectionRef}
      className={`relative py-20 px-6 ${className}`}
    >
      <div className="container mx-auto max-w-6xl">
        <div
          className={`text-center space-y-4 mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient">
            Our Process
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Structured approach from concept to field-ready delivery
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-primary transform -translate-x-1/2" />

          <div className="space-y-12">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`relative transition-all duration-1000 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
                onMouseEnter={() => setHoveredStep(step.id)}
                onMouseLeave={() => setHoveredStep(null)}
              >
                <div className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}>
                  <div className="flex-1 w-full">
                    <div className="glass-effect rounded-lg p-6 space-y-4 hover:scale-105 transition-transform duration-250">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon name={step.icon} size={24} className="text-primary" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-primary">
                            Step {step.number}
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">
                            {step.title}
                          </h3>
                        </div>
                      </div>

                      <p className="text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>

                      <div
                        className={`space-y-2 transition-all duration-500 ${
                          hoveredStep === step.id
                            ? 'max-h-96 opacity-100' :'max-h-0 opacity-0 overflow-hidden'
                        }`}
                      >
                        <h4 className="text-sm font-semibold text-foreground">
                          Key Activities:
                        </h4>
                        <ul className="space-y-1">
                          {step.details.map((detail, idx) => (
                            <li
                              key={idx}
                              className="flex items-start space-x-2 text-sm text-muted-foreground"
                            >
                              <Icon
                                name="ChevronRight"
                                size={16}
                                className="text-primary mt-0.5 flex-shrink-0"
                              />
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="hidden lg:flex w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary items-center justify-center text-primary-foreground font-bold text-xl shadow-glass z-10">
                    {step.number}
                  </div>

                  <div className="flex-1 w-full lg:invisible" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;